import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function ScopeOfWorkClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            Plain-English Explanation of Scope of Work Clause
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            A scope of work clause defines exactly what services or deliverables you're agreeing to provide, and often what's explicitly excluded.
          </p>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              This clause draws boundaries around your work. It lists what you will do, what you won't do, timelines, deliverables, and success criteria. A clear scope prevents misunderstandings about what's included and protects against endless revisions or extra requests.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Exists</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Scope clauses prevent "scope creep" where projects expand beyond original agreements. They create accountability for both parties and establish measurable completion criteria. Clear scopes reduce disputes about whether work was completed properly and whether additional requests require extra payment.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
            <ul className="space-y-3 ml-6">
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                You're only obligated to complete work within the defined scope
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Additional requests may require scope amendments and extra fees
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Provides grounds to decline out-of-scope requests
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Sets objective criteria for project completion
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What to Watch Out For</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Ensure the scope is specific enough to be enforceable but flexible enough for minor adjustments. Check how scope changes are handled, who approves them, and whether there's a change order process. Vague scopes create disputes while overly rigid ones cause friction.
            </p>
          </section>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Scope documents should be referenced in the main contract and updated through formal change procedures. Verbal agreements to expand scope may not be enforceable. This explanation provides general information but doesn't constitute legal advice about your specific situation.
            </p>
          </section>

          <section className="mb-12">
            <div className="flex gap-4 mb-8">
              <Link to="/clauses" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                ← All Clauses
              </Link>
              <Link to="/clauses/business" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                Business Clauses
              </Link>
            </div>
            <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/explain-payment-terms-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Payment Terms</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Understand payment schedules</p>
              </Link>
              <Link to="/explain-variation-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Variation Clause</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Learn about contract modifications</p>
              </Link>
            </div>
          </section>

          <div className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Placeholder */}
          <SponsorPlaceholder variant="full" />
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
